/**
 * Apple App Store + Google Play badges for the landing page.
 *
 * The native apps are not published yet, so these are deliberately
 * NON-INTERACTIVE: no `<a>`, no `href`, no click handler. A badge that links
 * nowhere (`href="#"`, or a store URL for an app that doesn't exist) is worse
 * than one that plainly reads "coming soon" — it 404s or bounces the user to
 * the top of the page. They render as `role="img"` with an accessible label
 * that states the coming-soon status, and are visually muted to match.
 *
 * When the apps ship: swap these recreations for the OFFICIAL badge artwork
 * (Apple's "Download on the App Store" lockup, Google's "Get it on Google
 * Play"), which both vendors require you to use unmodified and host yourself,
 * then wrap each in an `<a>` pointing at the real store listing.
 */

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0" fill="currentColor" aria-hidden="true">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" aria-hidden="true">
      <path
        d="M3.9 1.8c-.3.3-.5.8-.5 1.4v17.6c0 .6.2 1.1.5 1.4l.1.1 9.9-9.9v-.2L3.9 1.8z"
        fill="#00A0FF"
      />
      <path
        d="M17.3 15.7l-3.4-3.4v-.2l3.4-3.4.1.1 4 2.3c1.1.6 1.1 1.7 0 2.3l-4 2.3z"
        fill="#FFCE00"
      />
      <path d="M17.4 15.6L13.9 12.2 3.9 22.2c.4.4 1 .4 1.7.1l11.8-6.7" fill="#FF3A44" />
      <path d="M17.4 8.8L5.6 2.1c-.7-.4-1.3-.4-1.7.1l10 10 3.5-3.4z" fill="#00E676" />
    </svg>
  );
}

interface BadgeProps {
  label: string;
  caption: string;
  name: string;
  glyph: React.ReactNode;
}

function Badge({ label, caption, name, glyph }: BadgeProps) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex cursor-default select-none items-center gap-2.5 rounded-lg bg-neutral-900/85 px-4 py-2 text-white"
    >
      {glyph}
      <span className="flex flex-col leading-none">
        <span className="text-[10px] font-medium tracking-wide text-neutral-300">{caption}</span>
        <span className="mt-1 text-[15px] font-semibold tracking-tight">{name}</span>
      </span>
    </span>
  );
}

export default function AppStoreBadges({ className }: { className?: string }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className ?? ""}`}>
      <Badge
        label="Apple App Store, coming soon"
        caption="Coming soon"
        name="App Store"
        glyph={<AppleGlyph />}
      />
      <Badge
        label="Google Play, coming soon"
        caption="Coming soon"
        name="Google Play"
        glyph={<PlayGlyph />}
      />
    </div>
  );
}
